// components/features/tasks/TaskSaveModal.tsx
import { useState } from "react";
import { TaskForm, TaskFormValues } from "./TaskForm";
import { Task, Agent, ClientUser } from "../../../../types/features/support/Task";
import BaseModal from "../../../ui/Modal";
import { taskService } from "../../../../services/support/taskService";
import { uploadService } from "../../../../services/uploadService";
import useAuth from "../../../../hooks/auth/useAuth";
import useUsers from "../../../../hooks/administration/users/useUsers";
import { User } from "../../administration/users/UserDetailsModal";

interface TaskSaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (task: Task) => void;
  initialData?: Task | null;
}

export default function TaskSaveModal({ isOpen, onClose, onSuccess, initialData }: TaskSaveModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const isEdit = Boolean(initialData);
  const { users } = useUsers({});

  // Admins complets (pour le sélecteur + figer dans le payload)
  const admins: Agent[] = users
    .filter((u) => u.role === "ADMIN")
    .map((u) => ({
      id: u.id,
      email: u.email,
      name: `${u.last_name} ${u.first_name}`,
    }));

  // Clients complets (pour le sélecteur + figer dans le payload)
  const clients: ClientUser[] = users.map((u) => ({
    id: u.id,
    full_name: `${u.first_name} ${u.last_name}`,
    email: u.email,
    phone: u.phonenumber || "",
  }));

  // Creator figé (utilisateur courant)
  const creator: Agent | null = user
    ? {
        id: user.id,
        email: user.email,
        name: `${user.last_name} ${user.first_name}`,
      }
    : null;

  const handleSubmit = async (values: TaskFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1) Upload des fichiers
      let uploadedUrls: string[] = [];
      if (values.files.length > 0) {
        const uploadData = await uploadService.uploadMultiple({
          files: values.files,
          user_id: user?.id || "",
          type: "image",
        });
        uploadedUrls = uploadData.map((item) => item.url);
      }

      const attachments: string[] = [...values.existingAttachments, ...uploadedUrls];

      // 2) Résoudre les objets figés à partir des IDs sélectionnés
      const selectedAgents: Agent[] = values.agents
        .map((id) => admins.find((a) => a.id === id))
        .filter((a): a is Agent => Boolean(a));

      const selectedClient: ClientUser | null = values.client_id
        ? (clients.find((c) => c.id === values.client_id) ?? null)
        : null;

      // 3) Payload final
      const payload = {
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status,
        due_date: values.due_date,
        client_id: values.client_id,
        created_by: user?.id || "",
        creator,
        agents: selectedAgents,
        client: selectedClient,
        attachments,
        note: values.note, // ✅
      };

      // 4) Création ou mise à jour
      let savedTask: Task;
      if (isEdit && initialData) {
        savedTask = await taskService.updateTask(initialData.id, payload);
      } else {
        savedTask = await taskService.createTask(payload);
      }

      onSuccess?.(savedTask);
      onClose();
    } catch (err: any) {
      console.error(err.response);
      setError("Erreur lors de l'enregistrement de la consigne");
    } finally {
      setIsLoading(false);
    }
  };

  const title = initialData ? "Modifier la tâche" : "Nouvelle tâche";

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={title}>
      {error && (
        <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg mb-4 border border-red-200">{error}</div>
      )}

      <TaskForm
        clients={clients}
        initialData={initialData}
        admins={admins}
        onSubmit={handleSubmit}
        onCancel={onClose}
        loading={isLoading}
      />
    </BaseModal>
  );
}
